import globals from './globals'
import breakpoints from './breakpoints'
import { timing, easing } from './state'
import { isObject, mapKeys, clone, mapValues } from 'lodash'

let usableBp = breakpoints
delete usableBp.none

export const mixinCenter = {
  position: 'absolute',
  top: '0',
  bottom: '0',
  left: '0',
  right: '0',
  margin: 'auto',
  textAlign: 'center',
  height: '100%',
  width: '100%',
  display: 'grid',
  alignItems: 'center',
  alignContent: 'center',
  justifyContent: 'center',
}

export const mixinInlineContent = {
  display: 'inline-grid',
  gridAutoFlow: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  alignContent: 'center',
  gap: 1,
}

// export const mixinDashedBorder = {
//   backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%23DDE1E3FF' stroke-width='3' stroke-dasharray='6%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
//   borderRadius: `${globals.borderRadius.main}px`,
// }

const internalMixins = {
  makeResponsive: (prop, fn) =>
    isObject(prop) ? mapValues(prop, fn) : fn(prop),
  // Block load animation
  blockLoad: (props, position = 'relative') =>
    !props.children ||
    (props.isLoading &&
      `
    position: ${position};
    overflow: hidden;
    pointer-events: none;
    
    &:before {
      content: '';
      background-color: ${props.theme.palette.background.neutral_300.main};
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      z-index: ${props.theme.zIndex.secondary};
    }
    &:after {
      animation: wave 1.5s forwards infinite linear;
      background: linear-gradient(to left, ${
        props.theme.palette.background.neutral_300.main
      } 0%, ${props.theme.palette.background.paper.main} 33%, ${
        props.theme.palette.background.default.main
      } 66%, ${props.theme.palette.background.neutral_300.main} 100%) no-repeat;
      background-size: 50% 100%;
      content: '';
      position: absolute;
      top: 0;
      left: -200%;
      height: 100%;
      width: 100%;
      transform: skew(-20deg);
      z-index: ${props.theme.zIndex.tertiary};
    }
  `),
  fadeInLoad: (props, position = 'relative') => `
    ${
      !props.children || props.isLoading
        ? `
      position: ${position};
      overflow: hidden;
      pointer-events: none;
    `
        : ``
    }
    
    &:before {
      content: '';
      // background-color: ${props.theme.palette.background.neutral_300.main};
      background-color: ${props.theme.palette.background.paper.main};
      backdrop-filter: blur(6px);
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      z-index: ${props.theme.zIndex.secondary};
      transition: all .2s ease-in;
      opacity: ${!props.children || props.isLoading ? `1` : `0`}
    }
  `,
  // Breakpoints
  mqObj: (styles, min = false, max = false) => {
    const minWidth = min ? `(min-width: ${breakpoints[min]}px)` : ''
    const maxWidth = max ? `(max-width: ${breakpoints[max] - 1}px)` : ''
    if (min === 'xx' && !max) return styles
    return {
      [`@media ${minWidth} ${min && max ? ' and ' : ''} ${maxWidth}`]: {
        ...styles,
      },
    }
  },
  mq: (styles, min = false, max = false) => {
    const minWidth = min ? `(min-width: ${breakpoints[min]}px)` : ''
    const maxWidth = max ? `(max-width: ${breakpoints[max] - 1}px)` : ''
    if (min === 'xx' && !max) return styles
    return `@media ${minWidth} ${
      min && max ? ' and ' : ''
    } ${maxWidth} { ${styles} }`
  },
  ifTouch: (props = {}) =>
    'ontouchstart' in document.documentElement ? props : {},
  isTouch: 'ontouchstart' in document.documentElement,
  isSafari:
    navigator.userAgent.indexOf('Safari') !== -1 &&
    navigator.userAgent.indexOf('Chrome') === -1,
  isFirefox: navigator.userAgent.indexOf('Firefox') > -1,
  // Layout
  bg: (
    backgroundSize = 'cover',
    backgroundPosition = 'center',
    backgroundColor = ''
  ) => ({
    backgroundSize,
    backgroundPosition,
    backgroundColor,
    backgroundRepeat: 'no-repeat',
  }),
  spacing: factor => factor * globals.spaceBase,
  px: spacing => ({
    paddingLeft: `${internalMixins.spacing(spacing)}px`,
    paddingRight: `${internalMixins.spacing(spacing)}px`,
  }),
  py: spacing => ({
    paddingTop: `${internalMixins.spacing(spacing)}px`,
    paddingBottom: `${internalMixins.spacing(spacing)}px`,
  }),
  p: (spacing = []) => `
    padding: ${spacing
      .map(space => `${internalMixins.spacing(space)}px`)
      .join(' ')};
  `,
  mx: spacing => ({
    marginLeft: `${internalMixins.spacing(spacing)}px`,
    marginRight: `${internalMixins.spacing(spacing)}px`,
  }),
  my: spacing => ({
    marginTop: `${internalMixins.spacing(spacing)}px`,
    marginBottom: `${internalMixins.spacing(spacing)}px`,
  }),
  m: (spacing = []) => `
    margin: ${spacing
      .map(space => `${internalMixins.spacing(space)}px`)
      .join(' ')};
  `,
  columns: (options = {}) => ({
    display: options.inline ? 'inline-grid' : 'grid',
    gridAutoFlow: 'column',
    // gridTemplateColumns: options.gridAutoFlow !== 'column' ? options.gridAutoFlow : 'initial',
    alignItems: options.align || 'center',
    alignContent: options.align || 'center',
    justifyContent: options.justify || 'flex-start',
    gap: `${internalMixins.spacing(typeof options.gap === 'number' ? options.gap : 1)}px`,
  }),
  gridRows: (spacing = 0, inline = false, gridFlow = 'row') => ({
    display: inline ? 'inline-grid' : 'grid',
    gridAutoFlow: gridFlow === 'row' ? gridFlow : 'initial',
    gridTemplateRows: gridFlow !== 'row' ? gridFlow : 'initial',
    gap: `${internalMixins.spacing(spacing)}px`,
  }),
  flexRow: (inline = false) => ({
    display: inline ? 'inline-flex' : 'flex',
    flexDirection: 'row',
  }),
  flexColumn: (inline = false) => ({
    display: inline ? 'inline-flex' : 'flex',
    flexDirection: 'column',
  }),
  square: dimension => ({
    height: dimension,
    width: dimension,
  }),
  circle: dimension => ({
    height: dimension,
    width: dimension,
    borderRadius: '50%',
  }),
  // State
  setTransition: (
    transitions = [],
    time = timing.main,
    delay = 0,
    ease = easing.main
  ) =>
    transitions
      .map(transition => `${transition} ${time} ${delay}s ${ease}`)
      .join(', '),
  // Color
  hexToRgba: (hex, alpha = 1) => {
    let c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('')
      if (c.length == 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      c = `0x${c.join('')}`
      return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(
        ','
      )},${alpha})`
    }
  },
  center: mixinCenter,
  // dashedBorder: mixinDashedBorder,
}

export default internalMixins
